import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { func, object } from 'prop-types';
import get from 'lodash/get';
import { useStylitics, openMiniPDP } from 'yoda-core-components/lib/helpers/Stylitics';
import { detectSafari } from 'yoda-core-components/lib/helpers/Utils/Utils';
import {
    selectPreferences,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import { requestShopTheLookPricing } from 'yoda-site-components/lib/actions/ShopTheLookAction';
import { miniPDPOnClose } from 'yoda-site-components/lib/actions/MiniPDPAction';
import ProductConstants from 'yoda-site-components/lib/common/ProductConstants';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { shopThisLookClickEvent } from 'yoda-site-components/lib/actions/AnalyticsAction';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import User from 'yoda-site-components/lib/helpers/User/User';
import { ACCOUNT_ID } from 'yoda-site-components/lib/common/Constants';
import featureShopsConfig from './featureShops.config';

const STYLITICS_STYLE_OVERRIDES = `
.stylitics-look-cta { z-index: 7 !important; }
.stylitics-item-price { color: #202020 !important; font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; font-weight: 700 !important; }
.stylitics-item-sale-price { color: #202020 !important; font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; font-weight: 700 !important; }
.stylitics-item-price-slashed { color: #666666 !important; font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; font-weight: 400 !important; text-decoration: line-through !important; }
.promotional-sale-price { color: #E71324 !important; font-family: Open Sans,OpenSans,Helvetica,arial,sans-serif !important; font-weight: 700 !important; }
.promotional-text { font-style: italic !important; font-weight: 400 !important; color: #E71324 !important; }
`;

const getIndex = (arr, predicate) => Math.max(arr.findIndex(predicate), 0);

const FeatureShops = ({ location = {}, pageNameChange = null }) => {
    const dispatch = useDispatch();
    const preferences = useSelector(selectPreferences);
    const featureFlags = useSelector(selectFeatureFlags);
    const miniPDP = useSelector((state) => state.miniPDP || {});

    const outfitsRef = useRef([]);
    const containerRef = useRef(null);

    const slug = location.pathname?.split(ProductConstants.FEATURE_SHOP)?.[1] || '';
    const profileId = User?.isUserLoggedIn(true) ? TokenProvider.get(ACCOUNT_ID) : 'NA';

    // MiniPDP back-button handling
    useEffect(() => {
        if (miniPDP?.miniPDPSTL === 'open') {
            window.history.pushState(null, null, window.location.href);
            window.onpopstate = () => {
                detectSafari()
                    ? (window.history.scrollRestoration = 'manual')
                    : window.history.go(1);
                dispatch(miniPDPOnClose());
            };
        }
        if (miniPDP?.miniPDPSTL === 'close') {
            window.history.pushState(null, null, window.location.href);
            window.onpopstate = () => {
                window.history.go(-1);
            };
        }
    }, [miniPDP?.miniPDPSTL]);

    const handleOutfitsLoaded = useCallback((data) => {
        if (data && Object.keys(data).length > 0) {
            const bundleOutfits = get(data, 'look', {});
            outfitsRef.current = [...outfitsRef.current, bundleOutfits];
        }
    }, []);

    const handleItemClick = useCallback(
        (item) => {
            if (!featureFlags.enableFeatureShopsMiniPDP) return;

            // Normalize legacy SDK kebab-case to camelCase for openMiniPDP
            const normalizedItem = {
                remote_id: get(item, 'remote-id', '') || item?.remote_id || '',
                item_id: get(item, 'id', '') || item?.item_id || '',
                url: get(item, 'buy-url', '') || item?.url || '',
                other_client_item_ids: {
                    sku_nbrs:
                        get(item, 'other-client-item-ids.sku-nbrs', null) ||
                        item?.other_client_item_ids?.sku_nbrs,
                },
            };

            const opts = openMiniPDP(normalizedItem);
            if (opts) {
                const outfitID = get(item, 'outfit-id', '') || '';
                const itemID = outfitID || normalizedItem.item_id || '';
                const outfitData = outfitsRef.current.find((d) => d.id === outfitID);
                const itemIndex = outfitData
                    ? getIndex(outfitData.items, (i) => i.product_id === opts.ppId)
                    : getIndex(outfitsRef.current, (d) => d.id === itemID);

                dispatch(featureShopsConfig.miniPDPAction());
                dispatch(
                    featureShopsConfig.analyticsClickAction({
                        id: itemID,
                        ppId: opts.ppId,
                        moduleName: outfitID ? 'featured looks' : 'featured items',
                        linkPosition: itemIndex,
                    })
                );
            }
        },
        [featureFlags.enableFeatureShopsMiniPDP]
    );

    const handleOutfitClick = useCallback((e) => {
        const shopTheLookID = get(e, 'outfit-id', '');
        const linkPosition = getIndex(outfitsRef.current, (d) => d.id === shopTheLookID);

        if (shopTheLookID) {
            dispatch(
                shopThisLookClickEvent({
                    shopTheLookID,
                    moduleName: 'featured looks',
                    linkPosition,
                })
            );
        }

        const data = outfitsRef.current.find((d) => d.id === shopTheLookID);
        if (data?.items) {
            const productIds = data.items.map(
                (i) => i.product_id || i.remote_id?.split('_')[0] || ''
            );
            dispatch(requestShopTheLookPricing(productIds));
        }
    }, []);

    const handleShopView = useCallback(() => {
        const shopBlock = containerRef.current?.querySelectorAll('.stylitics-carousel-item-active');
        const pageNameData = shopBlock?.[0]?.ariaLabel;
        if (pageNameData && pageNameChange) {
            pageNameChange(pageNameData);
        }
    }, [pageNameChange]);

    const config = useMemo(
        () => ({
            ...featureShopsConfig,
            createWidget: async (itemNumber, ff) =>
                featureShopsConfig.createWidget(itemNumber, ff, {
                    slug,
                    profileId,
                    disableStyliticsColor: featureFlags.disableStyliticsColor,
                    onOutfitClick: handleOutfitClick,
                    onShopView: handleShopView,
                }),
        }),
        [slug, profileId, featureFlags.disableStyliticsColor, handleOutfitClick, handleShopView]
    );

    useStylitics(config, {
        preferences,
        featureFlags,
        onOutfitsLoaded: handleOutfitsLoaded,
        onItemClick: handleItemClick,
        onError: (err) => console.error('[FeatureShops] Init failed:', err),
    });

    // Inject style overrides on mount, cleanup on unmount
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = STYLITICS_STYLE_OVERRIDES;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    return (
        <div>
            <h4
                className={dt([
                    'text-center',
                    'font-open-sans-semibold',
                    '!leading-8',
                    'lg:text-xLg',
                    'sm:text-lg',
                    'pb-3',
                ])}
            >
                Feature Shops
            </h4>
            <div
                id="stylitics-featured-shops-target"
                className={dt([
                    'text-center',
                    'font-open-sans-medium',
                    '!text-base',
                    'leading-7',
                    'mb-10',
                ])}
                ref={containerRef}
            />
        </div>
    );
};

FeatureShops.propTypes = {
    location: object,
    pageNameChange: func,
};

export default FeatureShops;
